import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from './ui/select';

// X (formerly Twitter) icon component
const XIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Flat flag image URLs (using flagcdn.com for high-quality flat flags)
const FLAG_IMAGES = {
  SL: 'https://flagcdn.com/w40/lk.png',  // Sri Lanka
  UK: 'https://flagcdn.com/w40/gb.png',  // United Kingdom
  EU: 'https://flagcdn.com/w40/eu.png',  // European Union
};

// Flag component for footer - inline display
const FlagImage = ({ countryCode, className = '' }) => {
  const flagUrl = FLAG_IMAGES[countryCode] || FLAG_IMAGES.SL;
  return (
    <img 
      src={flagUrl} 
      alt={`${countryCode} flag`}
      className={`w-6 h-4 rounded-sm object-cover inline-block flex-shrink-0 mr-[5px] ${className}`}
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}
    />
  );
};

const Footer = () => {
  const { 
    country, 
    countryConfig, 
    setCountry, 
    allCountries,
    currency,
    currencyConfig,
    setCurrency,
    allCurrencies
  } = useCountry();

  const NEW_FOOTER = "Buy. Sell. Connect with confidence.\n\nZeilian™️ combines Trust Intelligence™️ and Market Intelligence™️ to help you make better decisions before you act.";

  const [footerContent, setFooterContent] = useState({
    footer_description: NEW_FOOTER
  });

  // State for disclaimer "more" expansion
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);

  // Disclaimer text parts
  const DISCLAIMER_SHORT = "Zeilian is an online platform that facilitates the publication and discovery of listings created by users and third parties. While reasonable care is taken to maintain the platform,";
  const DISCLAIMER_FULL = "Zeilian is an online platform that facilitates the publication and discovery of listings created by users and third parties. While reasonable care is taken to maintain the platform, Zeilian does not verify or guarantee the accuracy, legality, quality, or completeness of any listing, advertisement, or content submitted by users. Any transactions or communications between users and third parties are conducted at their own discretion and risk. To the fullest extent permitted by applicable law, Zeilian shall not be liable for any direct or indirect loss, damage, or claim arising from the use of the platform or reliance on any information contained within it.";

  // Dynamic footer config state
  const [footerConfig, setFooterConfig] = useState({
    enabled: true,
    contact: {
      enabled: true,
      title: "Contact Us",
      address: "123 Main Street, Colombo 00700, Sri Lanka",
      phone: "+94 11 234 5678",
      email: "support@zeilian.lk"
    },
    quick_links: {
      enabled: true,
      title: "Quick Links",
      links: [
        { label: "Home", url: "/" },
        { label: "Browse Ads", url: "/category/all" },
        { label: "Post Ad", url: "/post-ad" },
        { label: "Membership Plans", url: "/membership" },
        { label: "My Account", url: "/dashboard" }
      ]
    },
    categories: {
      enabled: true,
      title: "Categories",
      links: [
        { label: "Electronics", url: "/category/electronics" },
        { label: "Vehicles", url: "/category/vehicles" },
        { label: "Property", url: "/category/property" },
        { label: "Jobs", url: "/category/jobs" },
        { label: "Fashion", url: "/category/fashion" }
      ]
    }
  });

  // Fetch country-specific footer content (description) and full config
  const fetchFooterContent = useCallback(async () => {
    try {
      // Fetch content (description) and full footer config in parallel
      const [contentRes, footerRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/localization/public/content/${country}`),
        axios.get(`${API_URL}/api/admin/localization/public/footer/${country}`)
      ]);
      
      if (contentRes.data.success && contentRes.data.content) {
        setFooterContent(contentRes.data.content);
      }
      
      if (footerRes.data.success && footerRes.data.footer) {
        setFooterConfig(footerRes.data.footer);
      }
    } catch (err) {
      // Use default content
      setFooterContent({
        footer_description: NEW_FOOTER
      });
    }
  }, [country, countryConfig]);

  // Fetch content when country changes
  useEffect(() => {
    fetchFooterContent();
  }, [fetchFooterContent]);

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* About + Country/Currency Selectors */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl notranslate" translate="no">ZE</span>
              </div>
              <div className="flex items-start">
                <img 
                  src="/zeilian-logo.png" 
                  alt="Zeilian" 
                  className="h-14 w-auto object-contain"
                />
                <span className="text-[8px] text-gray-400 font-medium -ml-1 mt-1">™</span>
              </div>
            </div>
            {/* Multi-paragraph rendering with small ™ */}
            <div className="text-sm mb-4 space-y-2" data-testid="footer-description">
              {(footerContent.footer_description || '').split(/\n\n+/).map((para, i) => (
                <p key={i}>
                  {para.split(/(™️|™)/g).map((part, j) => (
                    part === '™️' || part === '™'
                      ? <sup key={j} className="text-[0.55em] align-super">™</sup>
                      : <span key={j}>{part}</span>
                  ))}
                </p>
              ))}
            </div>
            
            {/* Country & Currency Selectors */}
            <div className="space-y-3 mb-4">
              {/* Country Selector - with flat flag and proper spacing */}
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger 
                  className="w-full border border-gray-600 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg px-3 py-2.5 h-auto focus:ring-1 focus:ring-gray-500 [&>svg]:text-gray-400"
                  data-testid="footer-country-selector"
                >
                  <span className="inline-flex items-center gap-2 flex-nowrap">
                    <FlagImage countryCode={country} />
                    <span className="whitespace-nowrap">{countryConfig?.name}</span>
                  </span>
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {allCountries.map((c) => (
                    <SelectItem 
                      key={c.code} 
                      value={c.code}
                      className="text-gray-200 focus:bg-gray-700 focus:text-white"
                    >
                      <span className="inline-flex items-center gap-2 flex-nowrap">
                        <FlagImage countryCode={c.code} />
                        <span>{c.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Currency Selector - with proper spacing */}
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger 
                  className="w-full border border-gray-600 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg px-3 py-2.5 h-auto focus:ring-1 focus:ring-gray-500 [&>svg]:text-gray-400"
                  data-testid="footer-currency-selector"
                >
                  <span className="inline-flex items-center flex-nowrap whitespace-nowrap">
                    <span className="text-lg font-semibold">{currencyConfig?.symbol}</span>
                    <span className="ml-1">{currencyConfig?.code} - {currencyConfig?.name}</span>
                  </span>
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {allCurrencies?.map((c) => (
                    <SelectItem 
                      key={c.code} 
                      value={c.code}
                      className="text-gray-200 focus:bg-gray-700 focus:text-white"
                    >
                      <span className="inline-flex items-center flex-nowrap whitespace-nowrap">
                        <span className="text-lg font-semibold">{c.symbol}</span>
                        <span className="ml-1">{c.code} - {c.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quick Links - Dynamic */}
          {footerConfig.quick_links?.enabled !== false && (
            <div>
              <h3 className="text-white font-semibold text-lg mb-4" data-testid="footer-quick-links-title">
                {footerConfig.quick_links?.title || "Quick Links"}
              </h3>
              <ul className="space-y-2 text-sm" data-testid="footer-quick-links">
                {(footerConfig.quick_links?.links || []).map((link, idx) => (
                  <li key={idx}>
                    <Link to={link.url} className="hover:text-blue-400 transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Categories - Dynamic */}
          {footerConfig.categories?.enabled !== false && (
            <div>
              <h3 className="text-white font-semibold text-lg mb-4" data-testid="footer-categories-title">
                {footerConfig.categories?.title || "Categories"}
              </h3>
              <ul className="space-y-2 text-sm" data-testid="footer-categories">
                {(footerConfig.categories?.links || []).map((link, idx) => (
                  <li key={idx}>
                    <Link to={link.url} className="hover:text-blue-400 transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact - Dynamic */}
          {footerConfig.contact?.enabled !== false && (
            <div>
              <h3 className="text-white font-semibold text-lg mb-4" data-testid="footer-contact-title">
                {footerConfig.contact?.title || "Contact Us"}
              </h3>
              <ul className="space-y-3 text-sm" data-testid="footer-contact">
                {footerConfig.contact?.address && (
                  <li className="flex items-start space-x-2">
                    <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <span>{footerConfig.contact.address}</span>
                  </li>
                )}
                {footerConfig.contact?.phone && footerConfig.contact?.phone_enabled !== false && (
                  <li className="flex items-center space-x-2">
                    <Phone className="h-5 w-5 flex-shrink-0" />
                    <span>{footerConfig.contact.phone}</span>
                  </li>
                )}
                {footerConfig.contact?.email && (
                  <li className="flex items-center space-x-2">
                    <Mail className="h-5 w-5 flex-shrink-0" />
                    <span>{footerConfig.contact.email}</span>
                  </li>
                )}
              </ul>
              
              {/* Social Links - under Contact Us */}
              <div className="flex space-x-3 mt-4">
                <a href="#" className="w-9 h-9 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors">
                  <Facebook className="h-4 w-4" />
                </a>
                <a href="#" className="w-9 h-9 bg-gray-800 hover:bg-black rounded-full flex items-center justify-center transition-colors">
                  <XIcon className="h-4 w-4" />
                </a>
                <a href="#" className="w-9 h-9 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors">
                  <Instagram className="h-4 w-4" />
                </a>
                <a href="#" className="w-9 h-9 bg-gray-800 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors">
                  <Linkedin className="h-4 w-4" />
                </a>
              </div>

              {/* Disclaimer Text - under Contact Us */}
              {footerConfig.disclaimer?.enabled !== false && (
                <div className="text-xs text-gray-500 mt-4 leading-relaxed" data-testid="footer-disclaimer">
                  {disclaimerExpanded ? (
                    <>
                      <span>{DISCLAIMER_FULL}</span>
                      <button 
                        onClick={() => setDisclaimerExpanded(false)}
                        className="text-blue-400 hover:text-blue-300 ml-1 underline"
                      >
                        less
                      </button>
                    </>
                  ) : (
                    <>
                      <span>{DISCLAIMER_SHORT}</span>
                      <button 
                        onClick={() => setDisclaimerExpanded(true)}
                        className="text-blue-400 hover:text-blue-300 ml-1 underline"
                      >
                        more
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <Separator className="my-8 bg-gray-800" />

        <div className="flex flex-col md:flex-row justify-between items-center text-sm">
          <div className="text-center md:text-left">
            <p>&copy; 2026 Zeilian Ltd. All rights reserved.</p>
            <p className="text-xs text-gray-500 mt-1">Company No. 17084374 | ICO Reg. ZC105646</p>
          </div>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/disclaimer" className="hover:text-blue-400 transition-colors" data-testid="footer-disclaimer-link">Disclaimer</Link>
            <Link to="/privacy" className="hover:text-blue-400 transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-blue-400 transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
            <Link to="/help" className="hover:text-blue-400 transition-colors" data-testid="footer-help-link">Help Centre</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;